import React, { useEffect, useState, useCallback } from 'react';
import { Button, Alert, Collapse, Spinner } from 'react-bootstrap';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { API_BUSINESS } from '../../shared/consts/api';
import { formatAmount } from '../../shared/utils/formatAmount';

interface RateData {
  typeOperation: string;
  paymentMethod: string;
  comissionPercent: number;
  minAmountPayment: number;
  maxAmountPayment: number;
}

interface ProviderItem {
  idUnique: string;
  priority: number;
  provider: {
    name: string;
    idUnique: string;
  };
  product: {
    name: string;
    idUnique: string;
  };
}

interface SortableItemProps {
  item: ProviderItem;
  index: number;
  onDelete?: (id: string) => void;
  canRemove?: boolean;
  idBusiness: string;
  isProviderView?: boolean; // true = показываем мерчантов, false = показываем провайдеров
}

const SortableItem: React.FC<SortableItemProps> = ({ item, index, onDelete, canRemove, idBusiness, isProviderView = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [rates, setRates] = useState<RateData[]>([]);
  const [ratesLoaded, setRatesLoaded] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.idUnique });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const fetchRates = useCallback(async () => {
    if (ratesLoaded) return;

    setRatesLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const allRates: RateData[] = [];

      // Step 1: Get rate ID for provider
      const rateResponse = await fetch(`${API_BUSINESS}rgrfbusinessrfrate/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          listFields: [{
            fieldName: 'idrfBusiness',
            comparison: 'equalsStrong',
            listVal: [item.provider?.idUnique]
          }],
          sort: { fieldName: 'dateAdded', direction: 'desc' },
          take: 1,
          skip: 0
        }),
      });

      if (!rateResponse.ok) {
        setRatesLoaded(true);
        return;
      }

      const rateData = await rateResponse.json();
      const idrfRate = rateData.dataList?.[0]?.idrfRate;

      if (!idrfRate) {
        setRatesLoaded(true);
        return;
      }

      // Step 2: Get all rates by idrfRate
      const comissionResponse = await fetch(`${API_BUSINESS}drratecomission/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          listFields: [{
            fieldName: 'idrfRate',
            comparison: 'equalsStrong',
            listVal: [idrfRate]
          }],
          sort: null,
          searchData: '',
          take: 100,
          skip: 0
        }),
      });

      if (comissionResponse.ok) {
        const comissionData = await comissionResponse.json();
        if (comissionData.dataList) {
          for (const rate of comissionData.dataList) {
            allRates.push({
              typeOperation: rate.typeOperation,
              paymentMethod: rate.paymentMethod,
              comissionPercent: rate.comissionPercent,
              minAmountPayment: rate.minAmountPayment,
              maxAmountPayment: rate.maxAmountPayment,
            });
          }
        }
      }

      setRates(allRates);
      setRatesLoaded(true);
    } catch (err) {
      console.error('Error fetching rates:', err);
    } finally {
      setRatesLoading(false);
    }
  }, [item.provider?.idUnique, ratesLoaded]);

  const handleToggle = (e?: React.MouseEvent) => {
    if (!expanded && !ratesLoaded) {
      fetchRates();
    }
    setExpanded(!expanded);
  };

  const formatLimit = (min: number, max: number) => {
    const formatK = (n: number) => n >= 1000 ? `${Math.round(n / 1000)}K` : n.toString();
    return `${formatK(min)}-${formatK(max)}`;
  };

  const getMethodLabel = (method: string) => {
    switch (method) {
      case 'toCard': return 'Card';
      case 'sbp': return 'SBP';
      default: return method;
    }
  };

  // Group rates by typeOperation (API returns lowercase)
  const payInRates = rates.filter(r => r.typeOperation.toLowerCase() === 'payin');
  const payOutRates = rates.filter(r => r.typeOperation.toLowerCase() === 'payout');

  return (
    <div ref={setNodeRef} style={style} className="mb-1">
      <div
        className={`d-flex align-items-center p-2 rounded ${isDragging ? 'bg-secondary bg-opacity-25' : ''}`}
        onClick={handleToggle}
        style={{ cursor: 'pointer' }}
      >
        <div
          {...attributes}
          {...listeners}
          className="me-2 text-muted"
          style={{ cursor: 'grab', minWidth: '20px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <i className="fe fe-menu"></i>
        </div>
        <span className="text-muted me-2" style={{ minWidth: '24px' }}>{index + 1}.</span>
        <div className="flex-grow-1">
          {isProviderView
            ? (item.product?.name || 'Неизвестный мерчант')
            : (item.provider?.name || 'Неизвестный провайдер')
          }
        </div>
        <span className="text-muted me-2">
          <i className={`fe fe-chevron-${expanded ? 'up' : 'down'}`}></i>
        </span>
        {canRemove && onDelete && (
          <Button
            variant="link"
            size="sm"
            className="text-danger p-0"
            onClick={(e) => { e.stopPropagation(); onDelete(item.idUnique); }}
            title="Удалить"
          >
            <i className="fe fe-trash-2"></i>
          </Button>
        )}
      </div>

      <Collapse in={expanded}>
        <div className="ps-5 pe-2 pb-2 text-muted small" style={{ fontSize: '0.85em' }}>
          {ratesLoading ? (
            <div className="py-1">
              <Spinner animation="border" size="sm" className="me-2" />
              Загрузка...
            </div>
          ) : rates.length === 0 ? (
            <div className="py-1 text-muted">Нет данных о ставках</div>
          ) : (
            <>
              {payInRates.length > 0 && (
                <div className="py-1">
                  <span className="text-success">PayIn:</span>{' '}
                  {payInRates.map((r, i) => (
                    <span key={i}>
                      {formatAmount(r.comissionPercent)}% {formatLimit(r.minAmountPayment, r.maxAmountPayment)} {getMethodLabel(r.paymentMethod)}
                      {i < payInRates.length - 1 && ', '}
                    </span>
                  ))}
                </div>
              )}
              {payOutRates.length > 0 && (
                <div className="py-1">
                  <span className="text-warning">PayOut:</span>{' '}
                  {payOutRates.map((r, i) => (
                    <span key={i}>
                      {formatAmount(r.comissionPercent)}% {formatLimit(r.minAmountPayment, r.maxAmountPayment)} {getMethodLabel(r.paymentMethod)}
                      {i < payOutRates.length - 1 && ', '}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Collapse>
    </div>
  );
};

interface ProviderPriorityListProps {
  idBusiness: string;
  filterFieldName?: string;
  onClose?: () => void;
  canRemove?: boolean;
  forceUpdate?: boolean;
  onForceUpdateDone?: () => void;
}

const ProviderPriorityList: React.FC<ProviderPriorityListProps> = ({
  idBusiness,
  filterFieldName = 'idrfBusinessAsProduct',
  onClose,
  canRemove = true,
  forceUpdate = false,
  onForceUpdateDone
}) => {
  const isProviderView = filterFieldName === 'idrfBusinessAsProvider';
  const [items, setItems] = useState<ProviderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchProviders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`${API_BUSINESS}rgproductprovider/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          listFields: [
            {
              fieldName: filterFieldName,
              comparison: 'equalsStrong',
              listVal: [idBusiness],
            },
          ],
          sort: { fieldName: 'priority', direction: 'asc' },
          searchData: '',
          take: 100,
          skip: 0,
        }),
      });

      const data = await response.json();
      if (data.dataList) {
        setItems(data.dataList);
      }
    } catch (err) {
      setError('Ошибка загрузки провайдеров');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [idBusiness, filterFieldName]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  useEffect(() => {
    if (forceUpdate) {
      fetchProviders();
      if (onForceUpdateDone) onForceUpdateDone();
    }
  }, [forceUpdate, fetchProviders, onForceUpdateDone]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems((prevItems) => {
        const oldIndex = prevItems.findIndex((item) => item.idUnique === active.id);
        const newIndex = prevItems.findIndex((item) => item.idUnique === over.id);
        const newItems = arrayMove(prevItems, oldIndex, newIndex);
        return newItems;
      });
      setHasChanges(true);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('accessToken');

      const reorderItems = items.map((item, index) => ({
        idUnique: item.idUnique,
        priority: index,
      }));

      const response = await fetch(`${API_BUSINESS}rgproductprovider/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(reorderItems),
      });

      if (response.ok) {
        setHasChanges(false);
      } else {
        setError('Ошибка сохранения');
      }
    } catch (err) {
      setError('Ошибка сохранения');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (idUnique: string) => {
    if (!window.confirm('Удалить провайдера?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BUSINESS}rgproductprovider/${idUnique}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (response.ok) {
        setItems((prev) => prev.filter((item) => item.idUnique !== idUnique));
      } else {
        setError('Ошибка удаления');
      }
    } catch (err) {
      setError('Ошибка удаления');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Загрузка...</span>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Alert variant="info" className="mt-3">
        {isProviderView ? 'Нет привязанных мерчантов' : 'Нет привязанных провайдеров'}
      </Alert>
    );
  }

  return (
    <div className="mt-2">
      {hasChanges && (
        <div className="mb-2">
          <Button
            variant="success"
            size="sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>
      )}

      {error && <Alert variant="danger" className="mb-2 py-1">{error}</Alert>}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.idUnique)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item, index) => (
            <SortableItem
              key={item.idUnique}
              item={item}
              index={index}
              onDelete={handleDelete}
              canRemove={canRemove}
              idBusiness={idBusiness}
              isProviderView={isProviderView}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ProviderPriorityList;
